<template>
  <div class="flex-grow-1">
    <div class="mt-4" v-if="isLoading">
      <Spinner />
    </div>
    <a-row :gutter="20" v-if="!isLoading && !showEngine">
      <a-col :span="24">
        <div class="sub-descriptions">
          <h1>Try the new Javascript Malware Detection Engine!</h1>
          <p>We are pleased to launch our Javascript Malware Detection Engine! This engine will check the Javascript found on your website against signatures of known malicious Javascript such as:</p>
          <ul>
            <li>cryptominers (e.g. Coinhive)</li>
            <li>card skimmers (e.g. Magecart)</li>
            <li>other malicious scripts</li>
          </ul>
          <p>When this engine is enabled, you will be alerted to malicious Javascript and the type/family name assigned by the antimalware community, allowing you to quickly perform follow-up analysis and remediation. Here's an example:</p>
          <p><PaginationTable :columns="ExampleStaticMalwareScanColumns" :dataSource="ExampleStaticMalwareScanData"></PaginationTable></p>
          <p>
            <a-button size="default" class="success-btn" @click="startTrial" v-if="canStartFreeTrial">
              Start your non-obligatory 60 days free trial now!
            </a-button>
            <a-button size="default" v-else>
              Contact us to subscribe to this engine
            </a-button>
          </p>
          <p>Note that this engine will check Javascript regardless of policy settings.</p>
        </div>
      </a-col>
    </a-row>
    <a-row :gutter="20" v-if="!isLoading && showEngine">
      <a-col :span="24">
        <div class="sub-descriptions">
          <div class="description-highlight ant-alert-warning">
            <p v-if="engineTrialEndDate">Trial period till <b>{{ engineTrialEndDate | dateString }}</b></p>
            <p>We are excited to have you try out this new engine! Simply select the checkbox to enable the engine for this webpage. </p>
            <p style="margin-bottom: 0px;">We will love to hear your thoughts.  Please email custsupport@cloudsine.tech for any questions or suggestions about this new feature.</p>
          </div>
          <div>
            <p style="margin-top: 1em;">This engine will check the Javascript found on your website against signatures of known malicious Javascript such as cryptominers, card skimmers and other malicious scripts. </p>
            <p>When this engine is enabled, you will be alerted to malicious Javascript and the type/family name assigned by the antimalware community, allowing you to quickly perform follow-up analysis and remediation.</p>
            <p>Note that this engine will check Javascript regardless of policy settings.</p>
          </div>
        </div>
        <div>
          <a-checkbox v-model="isEnableStaticMalwareScan">
            Enable Javascript Malware Detection
          </a-checkbox>
        </div>
        <div style="margin-top: 20px;">
          <a-button size="large" class="success-btn" @click="verifyWebpage" :loading="isVerifying">
            <template v-if="!isVerifying">
              <div>Check for malicious scripts now!</div>
              <div style="font-size: 80%;">Saves settings and performs a verification of the webpage</div>
            </template>
            <template v-else>
              Saving settings and requesting verification of webpage
            </template>
          </a-button>
        </div>
      </a-col>
    </a-row>
    <a-row :gutter="20">
      <a-col :span="24">
        <!-- Add a little bit of space at the end of the page -->
        <br/><br/>
      </a-col>
    </a-row>

  </div>
</template>

<style lang="scss" scoped>
  .a-body {
    .sub-descriptions {
      background: #f7f7f7;
      overflow: hidden;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 20px;
    }
  }

  .description-highlight {
    padding: 15px;
  }
</style>

<script>
  import ApiService from '@shared/services/api.service';
  import WebpageService from "../services/webpage.service";
  import moment from "moment";

  const ExampleStaticMalwareScanColumns = [
    {
      title: 'No.',
      dataIndex: 'number',
      width: '150px'
    },
    {
      title: 'Script URL/Name',
      dataIndex: 'scriptName',
      slot: 'scriptName'
    },
    {
      title: 'Detection',
      dataIndex: 'detection',
      slot: 'detection'
    }
  ];

  const ExampleStaticMalwareScanData = [
    {
      'number': 1,
      'scriptName': 'example-coin-miner.js',
      'detection': 'Threat found: Application.BitCoinMiner.AJF'
    },
    {
      'number': 2,
      'scriptName': 'example-skimmer.js',
      'detection': 'Threat found: Trojware.HEUR'
    },
    {
      'number': 3,
      'scriptName': 'jquery.js',
      'detection': 'No threat found'
    }
  ];

  export default {
    name: 'StaticMalwareScan',
    components: {},
    data() {
      return {
        ExampleStaticMalwareScanColumns,
        ExampleStaticMalwareScanData,
        isLoading: true,
        isEnableStaticMalwareScan: false,
        isEngineSubscribed: null,
        isEngineTrial: null,
        engineTrialEndDate: null,
        isVerifying: false,
      };
    },
    mounted() {
      this.getDatas()
    },
    computed: {
      featureAccess() {
        return this.$store.state.user.data.feature_access;
      },
      canStartFreeTrial() {
        // We can only start the trial when there is no existing trial, and the user has not subscribed

        // For the whole trial thing, there are 4 cases:
        // 1. User just subscribe immediately without going to trial (this.isEngineSubscribed = true)
        // 2. User trialing the engine, and trial is still valid (this.isEngineTrial = true, this.engineTrialEndDate > now)
        // 3. User has trialed and trial is expired  (this.isEngineTrial = true, this.engineTrialEndDate <= now)
        // 4, User has trialed and subscribed (this.isEngineTrial = true, this.isEngineSubscribed = true)
        if (this.isEngineTrial || this.isEngineSubscribed) {
          return false;
        }

        return true;
      },
      showEngine() {
        if (this.isEngineSubscribed) {
          return true;
        }

        if (this.isEngineTrial && this.engineTrialEndDate) {
          const now = moment();
          if (moment(this.engineTrialEndDate).isAfter(now)) {
            return true;
          }
        }

        return false;
      }
    },
    methods: {
      async getDatas() {
        const webpageId = this.$route.params && this.$route.params.id;
        try {
          const [webpageData] = await Promise.all([WebpageService.get(+webpageId)]);
          this.isEnableStaticMalwareScan = webpageData.threshold && webpageData.threshold.enable_static_malware_scan;

          const trialStatus = await ApiService.get('trial_features/static_malware');

          if (trialStatus) {
            this.isEngineTrial = true;
            this.engineTrialEndDate = trialStatus.ends;
          }

          this.isEngineSubscribed = this.featureAccess['malware-static-engine'];
        } catch {
          this.$showErrorMessage('HTTP error received from web server!');
        } finally {
          if (this.isLoading) {
            this.isLoading = false;
          }
        }
      },
      verifyWebpage() {
        this.$emit('verify-webpage');
        this.isVerifying = true;
        // Because it is kinda complicated to sync up the actual verifying status from the parent to the child component
        // (and quite unnecessary anyway), we just "pretend" to wait for a few seconds to prevent the user from spamming
        // the button
        setTimeout(() => {this.isVerifying = false;}, 5000);
      },
      async startTrial() {
        this.isLoading = true;
        await ApiService.post('trial_features/static_malware/start_trial');
        await this.getDatas();
        this.isLoading = false;
      }

    },
  };
</script>


